import React from 'react';
import { shallowEqual } from 'react-redux';

import addSpreadOperation from 'editor/src/store/design/operation/spreadManipulation/addSpreadOperation';
import getPagesPerStep from 'editor/src/store/design/selector/getPagesPerStep';
import getSpread from 'editor/src/store/design/selector/getSpread';
import { legacyCanAddPagesForSpread } from 'editor/src/store/design/util/legacyPageCountRules';
import { canAddPagesForSpread, isAtMaxPageCount } from 'editor/src/store/design/util/pageCountControls';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import isPageCountOptionsEnabled from 'editor/src/store/hostSettings/selector/isPageCountOptionsEnabled';
import getECommercePageCounts from 'editor/src/store/variants/selector/getECommercePageCounts';

import { RootState } from 'editor/src/store';
import useTr from 'editor/src/util/useTr';

import { CanShow, MenuItemProps } from 'editor/src/component/Menu/type';
import usePageCountLimit from 'editor/src/component/TopMenuDesktop/usePageCountLimit';

import IconPlus from '../../Icon/IconPlus';
import WithTooltip from '../../WithTooltip';

import MenuButton from './MenuButton';

export function canShowAddRemovePages(state: RootState) {
  if (!state.design.designData?.page_count_limit) {
    return false;
  }

  if (state.hostSettings.ignorePersonalizationLock) {
    return true;
  }

  if (!isPageCountOptionsEnabled(state)) {
    // temporary fix until everyone can publish multiple variants for each photobook pages
    // there are 85 possible pages compbination
    return (state.variants.eCommerceProduct?.variants?.length ?? 0) >= 85;
  }

  // A buyer pays for the variant, so the page count can only move to another page count the
  // product sells alongside the rest of their selection.
  return (getECommercePageCounts(state)?.length ?? 0) > 1;
}

export const canShow: CanShow = (state, { hasSelection }) => {
  return !hasSelection && canShowAddRemovePages(state);
};

type Props = Pick<MenuItemProps, 'close' | 'mode' | 'spreadIndex'>;

function ButtonAddPages({ close, mode, spreadIndex }: Props) {
  const dispatch = useDispatch();
  const tr = useTr();
  const pageCountOptionsEnabled = useSelector(isPageCountOptionsEnabled);
  const { canAddAfter, designData, pagesPerStep } = useSelector((state) => {
    const { designData } = state.design;

    if (spreadIndex === undefined || spreadIndex === null || spreadIndex < 0) {
      throw new Error('spreadIndex is required');
    }

    const spread = getSpread(state, spreadIndex);
    return {
      canAddAfter: !!spread?.canAddAfter,
      designData,
      pagesPerStep: getPagesPerStep(state, spreadIndex, 'add'),
    };
  }, shallowEqual);

  const pageCountLimit = usePageCountLimit();
  const enable = pageCountOptionsEnabled
    ? canAddPagesForSpread(designData, canAddAfter, pageCountLimit, spreadIndex)
    : legacyCanAddPagesForSpread(designData, canAddAfter, pageCountLimit);

  const handleClick = () => {
    if (spreadIndex === undefined || spreadIndex === null || spreadIndex < 0) {
      throw new Error('spreadIndex is required');
    }
    dispatch(addSpreadOperation(spreadIndex));
    close?.();
  };

  const menuButton = (
    <MenuButton
      mode={mode}
      Icon={IconPlus}
      handleClick={handleClick}
      i18nKey="editor-add-pages-count"
      i18nOptions={{ count: pagesPerStep }}
      className="cy-button-add-pages"
      disabled={!enable}
    />
  );

  if (pageCountOptionsEnabled && pageCountLimit && isAtMaxPageCount(designData, pageCountLimit)) {
    return (
      <WithTooltip
        overlay={tr(
          'editor-cant-add-pages',
          "Can't add pages right now. This book can have at most {{pageNumber}} pages.",
          {
            pageNumber: pageCountLimit.max,
          },
        )}
        placement="top"
      >
        {menuButton}
      </WithTooltip>
    );
  }

  return menuButton;
}

export default React.memo(ButtonAddPages);
